import React, {useState} from 'react';

import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import {getScientificAIDisabledMessage} from 'model-hub/permissions/ModelHubPermissions';
import {BoltzGenFormContent} from 'model-hub/scientific-models/input-forms/boltzgen/BoltzGenInputForm';
import {DEFAULT_GENERATIVE_MODEL} from 'model-hub/scientific-models/ScientificModelConstants';
import {
  requireScientificModel,
  type ScientificModelNode,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {useScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import {CenteredSpinner} from 'ui-library/Spinner';
import * as logger from 'util/Logging';
import {assertNever} from 'util/Types';

import GenerativeModelRunDetails from './GenerativeModelRunDetails';
import {BoltzGenTargetType} from './GenerativeModelsConstants';
import {useGetGenerativeModelRuns, GenerativeModelsRunsList} from './GenerativeModelsRunsList';
import {type NormalizedRun} from './GenerativeModelsRunsList';

enum GenerativeModelsViewType {
  FORM = 'FORM',
  RUNS_LIST = 'RUNS_LIST',
  RUN_DETAILS = 'RUN_DETAILS',
}

interface GenerativeModelsViewProps {
  currentEntityType: BoltzGenTargetType;
  targetId: string;
}

export default function GenerativeModelsView({
  currentEntityType,
  targetId,
}: GenerativeModelsViewProps): JSX.Element | null {
  const result = useScientificModelRegistry();
  if (result.type === 'loading') {
    return <CenteredSpinner />;
  }
  if (result.type === 'error') {
    return <div>Failed to load models</div>;
  }
  const model = requireScientificModel(result.registry.catalogByExternalReference, DEFAULT_GENERATIVE_MODEL);
  return (
    <LoadedGenerativeModelsView
      currentEntityType={currentEntityType}
      isInstalled={result.registry.availableModelsByExternalReference.has(model.externalReference)}
      model={model}
      targetId={targetId}
    />
  );
}

export function LoadedGenerativeModelsView({
  currentEntityType,
  isInstalled,
  model,
  targetId,
}: GenerativeModelsViewProps & {
  isInstalled: boolean;
  model: ScientificModelNode;
}): JSX.Element | null {
  const [currentView, setCurrentView] = useState<GenerativeModelsViewType>(
    GenerativeModelsViewType.RUNS_LIST
  );
  const [selectedRun, setSelectedRun] = useState<NormalizedRun | null>(null);

  const {pendingRuns, completedRuns, loading, error, refetch} = useGetGenerativeModelRuns({
    currentEntityType,
    targetId,
  });

  function getDisabledMessage(): string | undefined {
    // Permission gating takes precedence over installation state: an AVAILABLE_DISABLED tenant
    // should always see the permission message, regardless of whether BoltzGen is installed.
    const permissionMessage = getScientificAIDisabledMessage();
    if (permissionMessage != null) {
      return permissionMessage;
    }
    if (!isInstalled) {
      return 'BoltzGen is not installed on this tenant. Ask your tenant admin to install it from the Scientific Models page.';
    }
    return undefined;
  }

  function renderListView(): JSX.Element {
    if (error) {
      return <div>Failed to load runs</div>;
    }
    return (
      <GenerativeModelsRunsList
        loading={loading}
        pendingRuns={pendingRuns}
        onRunSelected={(run: NormalizedRun) => {
          setSelectedRun(run);
          setCurrentView(GenerativeModelsViewType.RUN_DETAILS);
        }}
        completedRuns={completedRuns}
        onCreateNewRun={() => {
          setCurrentView(GenerativeModelsViewType.FORM);
        }}
        disabledMessage={getDisabledMessage()}
      />
    );
  }

  switch (currentView) {
    case GenerativeModelsViewType.FORM:
      return (
        <BoltzGenFormContent
          model={model}
          targetEntity={{entityType: currentEntityType, entityId: targetId}}
          navigateToList={() => {
            setCurrentView(GenerativeModelsViewType.RUNS_LIST);
          }}
          onSubmit={() => {
            refetch();
            setCurrentView(GenerativeModelsViewType.RUNS_LIST);
          }}
          source={MODEL_RUN_SOURCE.STRUCTURE_TAB}
          disabledMessage={getDisabledMessage()}
        />
      );
    case GenerativeModelsViewType.RUNS_LIST:
      return renderListView();
    case GenerativeModelsViewType.RUN_DETAILS:
      if (!selectedRun) {
        logger.warn('No selected run when switching to run details view');
        return renderListView();
      }
      return (
        <GenerativeModelRunDetails
          run={selectedRun}
          navigateToList={() => {
            setCurrentView(GenerativeModelsViewType.RUNS_LIST);
          }}
        />
      );
    default:
      assertNever(currentView);
      return null;
  }
}
